import type { SeriesLibraryDto } from '@dispositio/shared';
import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { errorMessage } from '../api/client';
import { useSeriesLibrary } from '../api/hooks';
import { de } from '../i18n/de';
import { classFocus } from '../lib/designModel';
import { AssetImg } from './AssetImg';
import { FocusIcon } from './design/DesignView';
import { ClassCheck } from './library/LibraryImport';
import { Modal } from './Modal';

const R = de.episodeRefs;
type Klass = SeriesLibraryDto['classes'][number];

/**
 * Fenster beim Anlegen einer Episode (PLAN.md §43): die Originale der früheren Episoden der Serie – je Element einmal, mit der Episode, in
 * der es entstand –, nach Klassen. Gewählt (zu Beginn alle) kommen sie als Referenzen in die neue Episode; was dort keine Rolle spielt,
 * wählt man ab. Haben die früheren Episoden keine Elemente, entsteht die Episode gleich.
 */
export function EpisodeReferences({ seriesId, seriesName, name, busy, onBack, onCreate }: { seriesId: string; seriesName: string; name: string; busy: boolean; onBack: () => void; onCreate: (references: string[]) => void }) {
  const lib = useSeriesLibrary(seriesId);
  /** abgewählte Elemente */
  const [off, setOff] = useState<ReadonlySet<string>>(new Set());
  const [closed, setClosed] = useState<ReadonlySet<string>>(new Set());
  const classes = useMemo(
    () =>
      (lib.data?.classes ?? [])
        .map((c) => ({ ...c, focus: classFocus({ id: c.name, name: c.name, builtin: c.builtin, color: c.color }) }))
        .sort((a, b) => a.focus.order - b.focus.order || a.focus.label.localeCompare(b.focus.label, 'de')),
    [lib.data],
  );
  const all = classes.flatMap((c) => c.entries.map((e) => e.id));
  const chosen = all.filter((id) => !off.has(id));
  const empty = !!lib.data && all.length === 0;
  // nichts zu übernehmen: gleich anlegen
  useEffect(() => {
    if (empty) onCreate([]);
  }, [empty]); // eslint-disable-line react-hooks/exhaustive-deps
  // während es lädt und wenn nichts zu übernehmen ist: kein Fenster
  if (empty || (!lib.data && !lib.error)) return null;

  const toggle = (ids: string[], on: boolean): void =>
    setOff((cur) => {
      const next = new Set(cur);
      for (const id of ids) {
        if (on) next.delete(id);
        else next.add(id);
      }
      return next;
    });
  const flip = (key: string): void =>
    setClosed((cur) => {
      const next = new Set(cur);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  const entryRow = (klass: Klass, e: Klass['entries'][number]) => {
    const title = e.title || de.libraryImport.untitled;
    return (
      <li key={e.id}>
        <label className="li-entry" data-testid="episode-ref" data-title={e.title}>
          <input type="checkbox" className="li-check" checked={!off.has(e.id)} onChange={(ev) => toggle([e.id], ev.target.checked)} />
          <span className="rb-avatar" style={{ '--c': klass.color } as CSSProperties}>
            {e.imageId ? <AssetImg id={e.imageId} alt="" loading="lazy" /> : <span>{title.slice(0, 1).toUpperCase()}</span>}
          </span>
          <span className="li-entry-title">{title}</span>
          <span className="li-entry-cat" title={R.fromEpisode(e.episode, lib.data!.episodes[e.episode - 1]?.name ?? '')}>
            {de.series.inherited(e.episode)}
          </span>
        </label>
      </li>
    );
  };

  return (
    <Modal
      title={R.title}
      onClose={onBack}
      wide
      footer={
        <>
          <button className="btn" onClick={onBack} data-testid="episode-refs-back">
            {R.back}
          </button>
          <span className="grow" />
          <button className="btn" onClick={() => onCreate([])} disabled={busy || !lib.data} data-testid="episode-refs-none">
            {R.none}
          </button>
          <button className="btn primary" onClick={() => onCreate(chosen)} disabled={busy || !lib.data} autoFocus data-testid="episode-refs-create">
            {R.create(chosen.length)}
          </button>
        </>
      }
    >
      <div className="library-import episode-refs" data-testid="episode-refs">
        {lib.error && <p className="error-text">{errorMessage(lib.error)}</p>}
        {lib.data && (
          <>
            <p className="muted small">{R.intro(seriesName, name)}</p>
            <div className="row episode-refs-tools">
              <button type="button" className="btn ghost small" onClick={() => setOff(new Set())} data-testid="episode-refs-all">
                {R.all}
              </button>
              <button type="button" className="btn ghost small" onClick={() => setOff(new Set(all))} data-testid="episode-refs-clear">
                {R.clear}
              </button>
              <span className="muted small">{de.libraryImport.picked(chosen.length, all.length)}</span>
            </div>
            <div className="li-tree">
              {classes.map((c) => {
                const ids = c.entries.map((e) => e.id);
                const n = ids.filter((id) => !off.has(id)).length;
                const expanded = !closed.has(c.name);
                const label = c.focus.label;
                return (
                  <section key={c.name} className="li-class" data-testid="episode-refs-class" data-class={c.name}>
                    <div className="li-class-row">
                      <ClassCheck all={n === ids.length} some={n > 0} label={de.libraryImport.wholeClass(label)} onChange={(on) => toggle(ids, on)} />
                      <button type="button" className="li-class-toggle" aria-expanded={expanded} onClick={() => flip(c.name)} title={expanded ? de.libraryImport.collapse(label) : de.libraryImport.expand(label)}>
                        <span className="li-class-icon">
                          <FocusIcon focus={c.focus} size={18} />
                        </span>
                        <span className="li-class-name">{label}</span>
                        <span className="li-count">{de.libraryImport.picked(n, ids.length)}</span>
                        <svg className={`li-chev${expanded ? ' open' : ''}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M9 6l6 6-6 6" />
                        </svg>
                      </button>
                    </div>
                    {expanded && <ul className="li-entries">{c.entries.map((e) => entryRow(c, e))}</ul>}
                  </section>
                );
              })}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
